import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';

import { createApiClient } from '../api.js';
import { useConfirm } from '../components/ConfirmDialog.jsx';
import { ChecklistPreview, ThemeScope } from './previews.jsx';
import {
  CheckIcon,
  EyeOffIcon,
  RefreshIcon,
  DeleteIcon,
} from '../components/icons';

export function ChecklistDetail() {
  const { checklist_id: checklistId } = useParams();
  const { getToken } = useAuth();
  const { project } = useOutletContext() || {};
  const api = useMemo(
    () => createApiClient(getToken, { projectId: project?.project_id || null }),
    [getToken, project?.project_id],
  );

  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const c = await api(`/v1/admin/checklists/${encodeURIComponent(checklistId)}`);
      setChecklist(c);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [api, checklistId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="state"><div className="state__title">Loading…</div></div>;
  if (error || !checklist) {
    return (
      <div className="state state--error">
        <div className="state__title">Couldn't load</div>
        <div>{error || `No checklist with id ${checklistId}`}</div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 4 }}>
      <div style={{ marginBottom: 12 }}>
        <Link to="../checklists" className="btn btn--ghost" style={{ padding: 0 }}>← All checklists</Link>
      </div>

      <header className="card" style={{ display: 'flex', gap: 24, justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--font-mono)' }}>
            <code>{checklist.checklist_id}</code>
          </div>
          <h2 style={{ margin: '4px 0 6px' }}>{checklist.title}</h2>
          {checklist.description && (
            <p style={{ margin: 0, color: 'var(--text-3)', fontSize: 13.5 }}>{checklist.description}</p>
          )}
        </div>
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          <span className={`badge ${(checklist.status === 'published') ? 'badge--editor' : 'badge--admin'}`}>
            {checklist.status}
          </span>
          <span className="badge badge--default">
            {checklist.items?.length || 0} item{(checklist.items?.length || 0) === 1 ? '' : 's'}
          </span>
          <ChecklistLifecycle checklist={checklist} api={api} onChanged={setChecklist} />
        </div>
      </header>

      <section className="card" style={{ marginTop: 14 }}>
        <h3 className="card__title">Preview</h3>
        <p className="card__body">
          How the checklist renders to the end user — one row per item,
          first item marked as done so you can see the checked state.
        </p>
        <ThemeScope className="theme-scope--preview">
          <ChecklistPreview checklist={checklist} />
        </ThemeScope>
      </section>

      <section className="card" style={{ marginTop: 14 }}>
        <h3 className="card__title">Items</h3>
        {!checklist.items?.length ? (
          <div className="list__meta" style={{ marginTop: 8 }}>No items yet.</div>
        ) : (
          <ul className="list" style={{ marginTop: 10 }}>
            {checklist.items.map((it, i) => (
              <li key={it.item_id || i} className="list__row list__row--muted">
                <div className="list__primary">
                  <span className="list__name">{it.title || it.label || <em style={{ color: 'var(--text-3)' }}>untitled</em>}</span>
                  <span className="list__meta">
                    {it.description && <>{it.description}</>}
                    {it.action_url && <> · <code>{it.action_url}</code></>}
                  </span>
                </div>
                {it.completion_event && (
                  <span className="badge badge--viewer">
                    completes on {it.completion_event}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function ChecklistLifecycle({ checklist, api, onChanged }) {
  const [busy, setBusy] = useState(null);
  const [confirmNode, ask] = useConfirm();
  const status = (checklist.status || '').toLowerCase();
  async function call(label, fn) {
    setBusy(label);
    try { onChanged(await fn()); } finally { setBusy(null); }
  }
  return (
    <div style={{ display: 'inline-flex', gap: 6 }}>
      {status === 'draft' && (
        <button type="button" className="btn btn--primary" disabled={busy !== null}
          onClick={() => call('publish', () => api(`/v1/admin/checklists/${encodeURIComponent(checklist.checklist_id)}/publish`, { method: 'POST' }))}>
          <CheckIcon /> {busy === 'publish' ? 'Publishing…' : 'Publish'}
        </button>
      )}
      {status === 'published' && (
        <button type="button" className="btn btn--ghost" disabled={busy !== null}
          onClick={() => call('unpublish', () => api(`/v1/admin/checklists/${encodeURIComponent(checklist.checklist_id)}`, { method: 'PUT', body: JSON.stringify({ status: 'draft' }) }))}>
          <EyeOffIcon /> {busy === 'unpublish' ? 'Unpublishing…' : 'Unpublish'}
        </button>
      )}
      {status === 'archived' ? (
        <button type="button" className="btn btn--ghost" disabled={busy !== null}
          onClick={() => call('restore', () => api(`/v1/admin/checklists/${encodeURIComponent(checklist.checklist_id)}`, { method: 'PUT', body: JSON.stringify({ status: 'draft' }) }))}>
          <RefreshIcon /> {busy === 'restore' ? 'Restoring…' : 'Restore'}
        </button>
      ) : (
        <button type="button" className="btn btn--danger-ghost" disabled={busy !== null}
          onClick={() => ask({
            title: `Archive "${checklist.title}"?`,
            body: 'Users stop seeing it; you can restore later.',
            confirmLabel: 'Archive',
            danger: true,
            runOnConfirm: () => call('archive', () => api(`/v1/admin/checklists/${encodeURIComponent(checklist.checklist_id)}/archive`, { method: 'POST' })),
          })}>
          <DeleteIcon />
          {busy === 'archive' ? 'Archiving…' : 'Archive'}
        </button>
      )}
      {confirmNode}
    </div>
  );
}
